import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  CpuChipIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
  MapPinIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import {
  useGetAllDevices,
  useGetAllLocations,
  useGetLiveData,
} from "../../queries/admin";
import { useIsAdmin } from "../../utils/auth";
import {
  useGetDeviceParameters,
  useCreateParameter,
  useUpdateParameter,
  useDeleteParameter,
} from "../../queries/parameters";
import { brokerUrl, topicPrefix } from "../../constant";
import useMqttStore from "../../store/useMqttStore";
import { LiveTable } from "../../components/widgets/table/LiveTable";
import {
  AddParameterModal,
  ParameterFormData,
} from "../../components/widgets/table/AddParameterModal";
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal";
import { ParameterData } from "../../types/device";
import { getParameterIcon } from "../../utils/parameterIcons";
import { toast } from "react-toastify";
import DeviceStatusBar from "../../components/layout/DeviceStatusBar";

export default function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const { data: devices, isLoading: devicesLoading } = useGetAllDevices();
  const { data: locations } = useGetAllLocations();
  const { data: parameters, isLoading: parametersLoading } =
    useGetDeviceParameters(id);
  const { mutate: deleteParameterMutation } = useDeleteParameter();

  const {
    connectMqtt,
    disconnectMqtt,
    isConnected,
    subscribeTopic,
    getDeviceData,
  } = useMqttStore();

  const [showAddParameterModal, setShowAddParameterModal] = useState(false);
  const [editParameter, setEditParameter] = useState<
    ParameterFormData | undefined
  >(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteParameterId, setDeleteParameterId] = useState<string | null>(
    null
  );
  const [showFullIdentifier, setShowFullIdentifier] = useState(false);

  // Find the device from the list
  const device = useMemo(() => {
    if (!devices || !id) return null;
    return devices.find((d: any) => d.id === id) || null;
  }, [devices, id]);

  // Get location name
  const locationName = useMemo(() => {
    if (!locations || !device?.locationId) return "\u2014";
    const loc = locations.find((l: any) => l.id === device.locationId);
    return loc?.name || "\u2014";
  }, [locations, device]);

  // Connect to MQTT
  useEffect(() => {
    connectMqtt(brokerUrl);
    return () => disconnectMqtt();
  }, []);

  // Subscribe to device topic
  useEffect(() => {
    if (id && isConnected) {
      const topic = `${topicPrefix}/${id}`;
      subscribeTopic(topic);
    }
  }, [id, isConnected]);

  // Get live data for this device
  const liveData = id ? getDeviceData(id) : null;

  // Last reading recorded in the DB — used as a fallback so the page shows the
  // device's most recent data (values and "Last data" time) when MQTT hasn't
  // delivered anything on this page load, instead of zeros / "No data received".
  const { data: lastKnownData } = useGetLiveData(id);

  // Merge live data into parameters
  const enrichedParameters: ParameterData[] = useMemo(() => {
    if (!parameters) return [];
    return parameters.map((param: any) => {
      // Prefer the live MQTT value; fall back to the last reading recorded in
      // the DB so a device that isn't currently publishing shows its most
      // recent data instead of zero. Both are raw device values — calibration
      // is applied downstream in LiveTable.
      const liveValue = liveData?.data?.[param.identifier];
      const dbValue = lastKnownData?.[param.identifier];
      const rawValue =
        liveValue !== undefined && liveValue !== null
          ? liveValue
          : dbValue !== undefined && dbValue !== null
          ? dbValue
          : undefined;
      const value = rawValue !== undefined ? Number(rawValue) : undefined;
      const calibratedValue =
        value !== undefined ? value * (param.calibration || 1) : undefined;

      let status: "normal" | "warning" | "critical" = "normal";
      if (
        calibratedValue !== undefined &&
        param.minValue != null &&
        param.maxValue != null
      ) {
        if (calibratedValue < param.minValue || calibratedValue > param.maxValue) {
          status = "critical";
        } else {
          const range = param.maxValue - param.minValue;
          const warningThreshold = range * 0.1;
          if (
            calibratedValue < param.minValue + warningThreshold ||
            calibratedValue > param.maxValue - warningThreshold
          ) {
            status = "warning";
          }
        }
      }

      return {
        ...param,
        value,
        status,
        trend: "stable" as const,
        trendValue: 0,
        iconElement: getParameterIcon(param.icon || param.identifier),
      };
    });
  }, [parameters, liveData, lastKnownData]);

  const handleEditParameter = (param: ParameterData) => {
    setEditParameter({
      id: param.id,
      deviceId: id || "",
      name: param.name,
      identifier: param.identifier,
      unit: param.unit,
      dataType: param.dataType,
      icon: param.icon,
      calibration: param.calibration,
      minValue: param.minValue,
      maxValue: param.maxValue,
    });
    setShowAddParameterModal(true);
  };

  const handleDeleteParameter = (parameterId?: string) => {
    if (!parameterId) return;
    setDeleteParameterId(parameterId);
    setShowDeleteModal(true);
  };

  const confirmDeleteParameter = () => {
    if (!deleteParameterId) return;
    deleteParameterMutation(deleteParameterId, {
      onSuccess: () => {
        toast.success("Parameter deleted successfully");
        setShowDeleteModal(false);
        setDeleteParameterId(null);
      },
      onError: () => {
        toast.error("Failed to delete parameter");
        setShowDeleteModal(false);
        setDeleteParameterId(null);
      },
    });
  };

  if (devicesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate("/devices")}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Devices
        </button>
        <div className="text-center py-12 bg-white dark:bg-[#111B21] rounded-lg border border-gray-200 dark:border-gray-700">
          <CpuChipIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
            Device not found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The device you are looking for does not exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/devices")}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Devices
        </button>

        {/* ========== Device Overview ========== */}
        <div className="bg-white dark:bg-[#111B21] rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CpuChipIcon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    {device.name}
                  </h1>
                  <p className="mt-0.5 flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                    {locationName}
                  </p>
                </div>
              </div>
              <DeviceStatusBar
                device={device}
                fallbackTimestamp={lastKnownData?.timestamp}
              />
            </div>
          </div>

          <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex flex-col items-start text-left">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Identifier
              </p>
              <div className="mt-0.5 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setShowFullIdentifier((v) => !v)}
                  title={
                    showFullIdentifier ? "Hide identifier" : "Show full identifier"
                  }
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  {showFullIdentifier ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
                <p className="text-sm font-mono font-medium text-gray-900 dark:text-white break-all">
                  {(() => {
                    const idValue = device.identifier || device.id || "\u2014";
                    if (showFullIdentifier) return idValue;
                    return idValue.length > 6
                      ? `${idValue.slice(0, 6)}\u2026`
                      : idValue;
                  })()}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-start text-left">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Data Rate
              </p>
              <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
                {device.dataRate
                  ? device.dataRate >= 3600
                    ? `Every ${device.dataRate / 3600}h`
                    : device.dataRate >= 60
                    ? `Every ${device.dataRate / 60}min`
                    : `Every ${device.dataRate}s`
                  : "\u2014"}
              </p>
            </div>
          </div>
        </div>

        {/* ========== Parameter Configuration ========== */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
             
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Parameter Configuration
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure and monitor device parameters
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={() => {
                  setEditParameter(undefined);
                  setShowAddParameterModal(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                Add Parameter
              </button>
            )}
          </div>

          {parametersLoading ? (
            <div className="flex items-center justify-center h-32 bg-white dark:bg-[#111B21] rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : enrichedParameters.length > 0 ? (
            <LiveTable
              parameters={enrichedParameters}
              isEditMode={isAdmin}
              onEditParameter={handleEditParameter}
              onDeleteParameter={handleDeleteParameter}
              isLiveDataConnected={isConnected}
            />
          ) : (
            <div className="text-center py-12 bg-white dark:bg-[#111B21] rounded-xl border border-gray-200 dark:border-gray-700">
              <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                No parameters configured
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add parameters to start monitoring this device.
              </p>
              {isAdmin && (
                <div className="mt-4">
                  <button
                    onClick={() => {
                      setEditParameter(undefined);
                      setShowAddParameterModal(true);
                    }}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Parameter
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Parameter Modal */}
      {showAddParameterModal && (
        <AddParameterModal
          isOpen={showAddParameterModal}
          onClose={() => {
            setShowAddParameterModal(false);
            setEditParameter(undefined);
          }}
          onSave={() => {
            setShowAddParameterModal(false);
            setEditParameter(undefined);
          }}
          deviceId={id}
          editParameter={
            editParameter
              ? { ...editParameter, deviceId: id || "" }
              : undefined
          }
          isEditMode={!!editParameter}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteParameterId(null);
          }}
          onConfirm={confirmDeleteParameter}
          title="Delete Parameter"
          itemName="this parameter"
          itemType="parameter"
        />
      )}
    </>
  );
}
